/**
 * Modal de configuração para node de reativar bot
 * Permite configurar mensagem opcional ao reativar
 * 
 * RELEASE: 1.0.0 - 2025-01-26 - Novo dialog para sistema de pausa de bot
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { TextareaWithVariables } from '../TextareaWithVariables';
import { FlowNode, ResumeBotConfig } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { HelpCircle } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';

// Schema de validação
const resumeBotSchema = z.object({
  resume_message: z.string().max(500).optional(),
});

interface ConfigureResumeBotDialogProps {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: ResumeBotConfig) => void;
}

/**
 * Dialog de configuração de reativar bot
 */
export function ConfigureResumeBotDialog({ tenantId, open, onOpenChange, node, onSave }: ConfigureResumeBotDialogProps) {
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  const [resumeMessage, setResumeMessage] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  // Inicializa valores do node
  useEffect(() => {
    if (node) {
      setResumeMessage(node.data.config?.resume_message || '');
    }
  }, [node]);

  /**
   * Salva a configuração
   */
  const handleSave = () => {
    if (!node) return;

    try {
      const config: ResumeBotConfig = {
        ...(resumeMessage && { resume_message: resumeMessage }),
        nickname: nicknameRef.current?.getNickname(),
      } as any;

      resumeBotSchema.parse(config);
      onSave(node.id, config);
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Configurar Reativar Bot</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setShowHelp(true)}
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* ID do Node (opcional, para referência) */}
          {node && <NodeIdField ref={nicknameRef} nodeId={node.id} defaultNickname={node?.data.config?.nickname} />}

          {/* Mensagem opcional */}
          <div className="space-y-2">
            <Label>Mensagem ao Reativar (opcional)</Label>
            <TextareaWithVariables
              tenantId={tenantId}
              value={resumeMessage}
              onChange={setResumeMessage}
              placeholder="Ex: O atendimento automático foi reativado. Como posso ajudar?"
              maxLength={500}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Mensagem enviada ao contato quando o bot for reativado
            </p>
          </div>

          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              💡 <strong>Dica:</strong> Este bloco é útil para cenários onde o usuário 
              escolhe voltar ao atendimento automático após conversar com um agente.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar
          </Button>
        </DialogFooter>

        {/* Dialog de ajuda */}
        <NodeHelpDialog
          open={showHelp}
          onOpenChange={setShowHelp}
          nodeType="resume_bot"
        />
      </DialogContent>
    </Dialog>
  );
}
